// src/worldCup2026Config.js
//
// FIFA World Cup 2026 — Bracket Pool Configuration
// Tournament: June 11 – July 19, 2026
// 48 teams, 12 groups of 4, 32 advance to knockouts (24 group winners/runners-up
// + 8 best third-placed teams), then R32 → R16 → QF → SF → Final.
//
// Used by: bracket_pool league format. Regular leagues do not import this file.
//
// Builds on: worldCup2026Config-wc-cleanup-20260526.js
// Adds: KNOCKOUT_MATCHUPS (R32–FINAL skeleton from FIFA's Dec 5, 2025 draw),
//       position-code helpers, getMatchupBySlot, getMatchTeams, formatPositionLabel.
// R32 bracket source: USA TODAY / FIFA draw coverage, Dec 5, 2025.

// ---------- Tournament metadata ----------

export const WORLD_CUP_2026 = {
  id: "world-cup-2026",
  name: "FIFA World Cup 2026",
  shortName: "World Cup 2026",
  startDate: "2026-06-11",
  endDate: "2026-07-19",
  totalTeams: 48,
  totalGroups: 12,
  teamsPerGroup: 4,
  // Single hard lock at opening kickoff. Update once FIFA confirms exact UTC time.
  // Placeholder is 8pm UTC on June 11 (Mexico hosts the opener).
  predictionLockAt: "2026-06-11T20:00:00Z",
};

// ---------- Teams (keyed by FIFA 3-letter code) ----------

export const TEAMS = {
  // Group A
  MEX: { name: "Mexico",                 abbr: "MEX", flag: "🇲🇽" },
  RSA: { name: "South Africa",           abbr: "RSA", flag: "🇿🇦" },
  KOR: { name: "South Korea",            abbr: "KOR", flag: "🇰🇷" },
  CZE: { name: "Czechia",                abbr: "CZE", flag: "🇨🇿" },

  // Group B
  CAN: { name: "Canada",                 abbr: "CAN", flag: "🇨🇦" },
  BIH: { name: "Bosnia and Herzegovina", abbr: "BIH", flag: "🇧🇦" },
  QAT: { name: "Qatar",                  abbr: "QAT", flag: "🇶🇦" },
  SUI: { name: "Switzerland",            abbr: "SUI", flag: "🇨🇭" },

  // Group C
  BRA: { name: "Brazil",                 abbr: "BRA", flag: "🇧🇷" },
  MAR: { name: "Morocco",                abbr: "MAR", flag: "🇲🇦" },
  HAI: { name: "Haiti",                  abbr: "HAI", flag: "🇭🇹" },
  SCO: { name: "Scotland",               abbr: "SCO", flag: "🏴󠁧󠁢󠁳󠁣󠁴󠁿" },

  // Group D
  USA: { name: "United States",          abbr: "USA", flag: "🇺🇸" },
  PAR: { name: "Paraguay",               abbr: "PAR", flag: "🇵🇾" },
  AUS: { name: "Australia",              abbr: "AUS", flag: "🇦🇺" },
  TUR: { name: "Türkiye",                abbr: "TUR", flag: "🇹🇷" },

  // Group E
  GER: { name: "Germany",                abbr: "GER", flag: "🇩🇪" },
  CUW: { name: "Curaçao",                abbr: "CUW", flag: "🇨🇼" },
  CIV: { name: "Ivory Coast",            abbr: "CIV", flag: "🇨🇮" },
  ECU: { name: "Ecuador",                abbr: "ECU", flag: "🇪🇨" },

  // Group F
  NED: { name: "Netherlands",            abbr: "NED", flag: "🇳🇱" },
  JPN: { name: "Japan",                  abbr: "JPN", flag: "🇯🇵" },
  SWE: { name: "Sweden",                 abbr: "SWE", flag: "🇸🇪" },
  TUN: { name: "Tunisia",                abbr: "TUN", flag: "🇹🇳" },

  // Group G
  BEL: { name: "Belgium",                abbr: "BEL", flag: "🇧🇪" },
  EGY: { name: "Egypt",                  abbr: "EGY", flag: "🇪🇬" },
  IRN: { name: "Iran",                   abbr: "IRN", flag: "🇮🇷" },
  NZL: { name: "New Zealand",            abbr: "NZL", flag: "🇳🇿" },

  // Group H
  ESP: { name: "Spain",                  abbr: "ESP", flag: "🇪🇸" },
  CPV: { name: "Cape Verde",             abbr: "CPV", flag: "🇨🇻" },
  KSA: { name: "Saudi Arabia",           abbr: "KSA", flag: "🇸🇦" },
  URU: { name: "Uruguay",                abbr: "URU", flag: "🇺🇾" },

  // Group I
  FRA: { name: "France",                 abbr: "FRA", flag: "🇫🇷" },
  SEN: { name: "Senegal",                abbr: "SEN", flag: "🇸🇳" },
  IRQ: { name: "Iraq",                   abbr: "IRQ", flag: "🇮🇶" },
  NOR: { name: "Norway",                 abbr: "NOR", flag: "🇳🇴" },

  // Group J
  ARG: { name: "Argentina",              abbr: "ARG", flag: "🇦🇷" },
  ALG: { name: "Algeria",                abbr: "ALG", flag: "🇩🇿" },
  AUT: { name: "Austria",                abbr: "AUT", flag: "🇦🇹" },
  JOR: { name: "Jordan",                 abbr: "JOR", flag: "🇯🇴" },

  // Group K
  POR: { name: "Portugal",               abbr: "POR", flag: "🇵🇹" },
  COD: { name: "DR Congo",               abbr: "COD", flag: "🇨🇩" },
  UZB: { name: "Uzbekistan",             abbr: "UZB", flag: "🇺🇿" },
  COL: { name: "Colombia",               abbr: "COL", flag: "🇨🇴" },

  // Group L
  ENG: { name: "England",                abbr: "ENG", flag: "🏴󠁧󠁢󠁥󠁮󠁧󠁿" },
  CRO: { name: "Croatia",                abbr: "CRO", flag: "🇭🇷" },
  GHA: { name: "Ghana",                  abbr: "GHA", flag: "🇬🇭" },
  PAN: { name: "Panama",                 abbr: "PAN", flag: "🇵🇦" },
};

// ---------- Groups ----------

export const GROUPS = {
  A: ["MEX", "RSA", "KOR", "CZE"],
  B: ["CAN", "BIH", "QAT", "SUI"],
  C: ["BRA", "MAR", "HAI", "SCO"],
  D: ["USA", "PAR", "AUS", "TUR"],
  E: ["GER", "CUW", "CIV", "ECU"],
  F: ["NED", "JPN", "SWE", "TUN"],
  G: ["BEL", "EGY", "IRN", "NZL"],
  H: ["ESP", "CPV", "KSA", "URU"],
  I: ["FRA", "SEN", "IRQ", "NOR"],
  J: ["ARG", "ALG", "AUT", "JOR"],
  K: ["POR", "COD", "UZB", "COL"],
  L: ["ENG", "CRO", "GHA", "PAN"],
};

export const GROUP_LETTERS = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L"];

// ---------- Knockout structure ----------

export const KNOCKOUT_ROUNDS = ["R32", "R16", "QF", "SF", "FINAL"];

export const KNOCKOUT_SLOTS = {
  R32: 16,   // 16 matchups, 32 teams
  R16: 8,
  QF:  4,
  SF:  2,
  FINAL: 1,
};

// ---------- Scoring ----------

export const SCORING = {
  // Group stage: 5 pts per correctly-placed team (1st-4th in a group)
  // 4 slots × 12 groups = 48 placements × 5 = 240 max
  GROUP_STAGE_PER_SLOT: 5,
  GROUP_STAGE_MAX: 240,

  // Knockout winners — each round escalates: 5 → 15 → 30 → 60
  R32_WINNER: 5,    // 16 × 5  =  80 max
  R16_WINNER: 15,   //  8 × 15 = 120 max
  QF_WINNER:  30,   //  4 × 30 = 120 max
  SF_WINNER:  60,   //  2 × 60 = 120 max
  CHAMPION:   200,  //  1 × 200 = 200 max
  GOLDEN_BOOT: 120, //  1 × 120 = 120 max

  TOTAL_MAX: 1000,
};

// Per-round points lookup. Used by scoring code so we don't repeat
// the round→constant mapping in multiple places.
export const ROUND_POINTS_PER_WIN = {
  R32:   SCORING.R32_WINNER,
  R16:   SCORING.R16_WINNER,
  QF:    SCORING.QF_WINNER,
  SF:    SCORING.SF_WINNER,
  FINAL: SCORING.CHAMPION,
};

// ---------- Knockout round unlock schedule ----------

// Knockout round unlock timestamps. Each round's picks become available
// when the previous round's last match concludes (i.e., real matchups are
// known). Pre-Phase-2, these gates are date-based; Phase 2 moves to
// per-matchup unlocks driven by live API data as fixtures are confirmed.
//
// Schedule reference (FIFA confirmed June 11 - July 19 2026):
//   Group stage:    June 11 - June 27
//   Round of 32:    June 28 - July 3
//   Round of 16:    July 4  - July 7
//   Quarterfinals:  July 9  - July 11
//   Semifinals:     July 14 - July 15
//   Final:          July 19
export const KNOCKOUT_UNLOCK_DATES = {
  R32:   "2026-06-28T00:00:00Z",  // Day after group stage ends
  R16:   "2026-07-04T00:00:00Z",  // After R32 finishes
  QF:    "2026-07-09T00:00:00Z",  // After R16 finishes
  SF:    "2026-07-14T00:00:00Z",  // After QF finishes
  FINAL: "2026-07-19T00:00:00Z",  // Day of the Final
};

// Display labels for each round (used in headers, status pills, etc.)
export const KNOCKOUT_ROUND_LABELS = {
  R32:   "Round of 32",
  R16:   "Round of 16",
  QF:    "Quarterfinals",
  SF:    "Semifinals",
  FINAL: "Final",
};

// ---------- Knockout matchup skeleton ----------
//
// Source: USA TODAY / FIFA bracket published Dec 5, 2025 after the official draw.
// Cross-checked against Estadio Azteca ticket listings for Match 79.
//
// Side shape (used for `home` and `away` on every matchup):
//   • string "1A"…"2L"       → group position (1=winner, 2=runner-up)
//   • { thirdPlace: [...] }  → best third-placed team from one of N groups,
//                              resolved by FIFA tiebreakers after group stage.
//                              The actual team for each R32 slot is recorded
//                              in liveStandings.knockouts.thirdPlaceAssignments,
//                              keyed by R32 slot.
//   • { feedFrom: "R32-1" }  → winner of a previous match (R16 and later).
//
// Slot IDs: ordered by FIFA match number (R32-1 = Match 73 … R32-16 = Match 88).
// Each matchup also carries `feedsInto` for the next round's slot, so the
// bracket can be traversed in either direction.
//
// Pattern summary (R32):
//   • 8 group winners face a third-placer:  A, B, D, E, G, I, K, L
//   • 4 winners face a runner-up (cross-paired): 1C↔2F, 1F↔2C, 1H↔2J, 1J↔2H
//   • 4 runner-up vs runner-up:  2A-2B, 2D-2G, 2E-2I, 2K-2L
//
// Match numbers: R32 = 73–88, R16 = 89–96, QF = 97–100, SF = 101–102,
// 3rd-place = 103 (not scored by this bracket pool), Final = 104.

export const KNOCKOUT_MATCHUPS = {
  R32: [
    { slot: "R32-1",  matchNumber: 73, home: "2A",                                away: "2B",                                venue: "Los Angeles",            feedsInto: "R16-2" },
    { slot: "R32-2",  matchNumber: 74, home: "1E",                                away: { thirdPlace: ["A","B","C","D","F"] }, venue: "Boston",                 feedsInto: "R16-1" },
    { slot: "R32-3",  matchNumber: 75, home: "1F",                                away: "2C",                                venue: "Guadalajara",            feedsInto: "R16-2" },
    { slot: "R32-4",  matchNumber: 76, home: "1C",                                away: "2F",                                venue: "Houston",                feedsInto: "R16-3" },
    { slot: "R32-5",  matchNumber: 77, home: "1I",                                away: { thirdPlace: ["C","D","F","G","H"] }, venue: "New York/New Jersey",    feedsInto: "R16-1" },
    { slot: "R32-6",  matchNumber: 78, home: "2E",                                away: "2I",                                venue: "Dallas",                 feedsInto: "R16-3" },
    { slot: "R32-7",  matchNumber: 79, home: "1A",                                away: { thirdPlace: ["C","E","F","H","I"] }, venue: "Mexico City",            feedsInto: "R16-4" },
    { slot: "R32-8",  matchNumber: 80, home: "1L",                                away: { thirdPlace: ["E","H","I","J","K"] }, venue: "Atlanta",                feedsInto: "R16-4" },
    { slot: "R32-9",  matchNumber: 81, home: "1D",                                away: { thirdPlace: ["B","E","F","I","J"] }, venue: "San Francisco Bay Area", feedsInto: "R16-6" },
    { slot: "R32-10", matchNumber: 82, home: "1G",                                away: { thirdPlace: ["A","E","H","I","J"] }, venue: "Seattle",                feedsInto: "R16-6" },
    { slot: "R32-11", matchNumber: 83, home: "2K",                                away: "2L",                                venue: "Toronto",                feedsInto: "R16-5" },
    { slot: "R32-12", matchNumber: 84, home: "1H",                                away: "2J",                                venue: "Los Angeles",            feedsInto: "R16-5" },
    { slot: "R32-13", matchNumber: 85, home: "1B",                                away: { thirdPlace: ["E","F","G","I","J"] }, venue: "Vancouver",              feedsInto: "R16-8" },
    { slot: "R32-14", matchNumber: 86, home: "1J",                                away: "2H",                                venue: "Miami",                  feedsInto: "R16-7" },
    { slot: "R32-15", matchNumber: 87, home: "1K",                                away: { thirdPlace: ["D","E","I","J","L"] }, venue: "Kansas City",            feedsInto: "R16-8" },
    { slot: "R32-16", matchNumber: 88, home: "2D",                                away: "2G",                                venue: "Dallas",                 feedsInto: "R16-7" },
  ],
  R16: [
    { slot: "R16-1", matchNumber: 89, home: { feedFrom: "R32-2"  }, away: { feedFrom: "R32-5"  }, venue: "Philadelphia",          feedsInto: "QF-1" },
    { slot: "R16-2", matchNumber: 90, home: { feedFrom: "R32-1"  }, away: { feedFrom: "R32-3"  }, venue: "Houston",               feedsInto: "QF-1" },
    { slot: "R16-3", matchNumber: 91, home: { feedFrom: "R32-4"  }, away: { feedFrom: "R32-6"  }, venue: "New York/New Jersey",   feedsInto: "QF-3" },
    { slot: "R16-4", matchNumber: 92, home: { feedFrom: "R32-7"  }, away: { feedFrom: "R32-8"  }, venue: "Mexico City",           feedsInto: "QF-3" },
    { slot: "R16-5", matchNumber: 93, home: { feedFrom: "R32-11" }, away: { feedFrom: "R32-12" }, venue: "Dallas",                feedsInto: "QF-2" },
    { slot: "R16-6", matchNumber: 94, home: { feedFrom: "R32-9"  }, away: { feedFrom: "R32-10" }, venue: "Seattle",               feedsInto: "QF-2" },
    { slot: "R16-7", matchNumber: 95, home: { feedFrom: "R32-14" }, away: { feedFrom: "R32-16" }, venue: "Atlanta",               feedsInto: "QF-4" },
    { slot: "R16-8", matchNumber: 96, home: { feedFrom: "R32-13" }, away: { feedFrom: "R32-15" }, venue: "Vancouver",             feedsInto: "QF-4" },
  ],
  QF: [
    { slot: "QF-1", matchNumber: 97,  home: { feedFrom: "R16-1" }, away: { feedFrom: "R16-2" }, venue: "Boston",        feedsInto: "SF-1" },
    { slot: "QF-2", matchNumber: 98,  home: { feedFrom: "R16-5" }, away: { feedFrom: "R16-6" }, venue: "Los Angeles",   feedsInto: "SF-1" },
    { slot: "QF-3", matchNumber: 99,  home: { feedFrom: "R16-3" }, away: { feedFrom: "R16-4" }, venue: "Miami",         feedsInto: "SF-2" },
    { slot: "QF-4", matchNumber: 100, home: { feedFrom: "R16-7" }, away: { feedFrom: "R16-8" }, venue: "Kansas City",   feedsInto: "SF-2" },
  ],
  SF: [
    { slot: "SF-1", matchNumber: 101, home: { feedFrom: "QF-1" }, away: { feedFrom: "QF-2" }, venue: "Dallas (AT&T Stadium)",      feedsInto: "FINAL-1" },
    { slot: "SF-2", matchNumber: 102, home: { feedFrom: "QF-3" }, away: { feedFrom: "QF-4" }, venue: "Atlanta (Mercedes-Benz)",    feedsInto: "FINAL-1" },
  ],
  FINAL: [
    { slot: "FINAL-1", matchNumber: 104, home: { feedFrom: "SF-1" }, away: { feedFrom: "SF-2" }, venue: "MetLife Stadium (East Rutherford, NJ)", feedsInto: null },
  ],
};

// ---------- League format identifiers ----------

// Set on the league document as `format`. Distinguishes bracket-pool leagues
// from regular wagering leagues. Future bracket pools (Euro 2028 etc.) reuse
// this format value with a different TEMPLATE_ID.
export const LEAGUE_FORMAT = "bracket_pool";

// Set on the league document as `templateId` (matches existing handleCreate).
// Identifies *which* bracket pool — separate from format so we can add other
// bracket pools later (Euro 2028, March Madness) without overloading the format.
export const TEMPLATE_ID = "world-cup-2026";

// ---------- Helpers ----------

/** Look up team metadata by 3-letter code. Returns null if not found. */
export function getTeam(code) {
  if (!code) return null;
  return TEAMS[code] ?? null;
}

/** Get the array of team codes in a given group letter (A-L). */
export function getGroupTeams(letter) {
  return GROUPS[letter] ?? [];
}

/** Find which group letter a given team code belongs to. Returns null if not found. */
export function getGroupForTeam(code) {
  for (const [letter, codes] of Object.entries(GROUPS)) {
    if (codes.includes(code)) return letter;
  }
  return null;
}

/** All 48 team codes, flat. */
export function getAllTeamCodes() {
  return Object.values(GROUPS).flat();
}

/** True if this league document represents the World Cup 2026 bracket pool.
 *  Lenient detection — matches if EITHER the format flag OR the templateId
 *  identifies this as the WC2026 bracket pool. This handles cases where a
 *  cached league object is missing one field (e.g. loadMyLeagues projection,
 *  or older leagues created before the format flag was wired). */
export function isWorldCup2026League(league) {
  if (!league) return false;
  return league.format === LEAGUE_FORMAT
      || league.templateId === TEMPLATE_ID;
}

/** True if predictions are still open (i.e. tournament has not yet kicked off). */
export function predictionsOpen(now = new Date()) {
  return now < new Date(WORLD_CUP_2026.predictionLockAt);
}

/** True if knockout picks can currently be written. Group predictions lock at
 *  kickoff, but knockout rounds open progressively during the tournament, so
 *  saving must stay enabled whenever any knockout round has unlocked. Mirrors
 *  the Firestore rule, which permits post-kickoff writes as long as the frozen
 *  fields (groups / goldenBoot / totalGoals) are unchanged. */
export function knockoutPicksOpen(now = new Date()) {
  return anyKnockoutUnlocked(now);
}

/** True if ANY prediction write should be allowed right now — either the
 *  pre-kickoff full-edit window, or the post-kickoff knockout window. */
export function predictionsWritable(now = new Date()) {
  return predictionsOpen(now) || knockoutPicksOpen(now);
}

/** Returns unlock status for a knockout round.
 *  { isUnlocked, unlockAt, daysUntil, label, dateLabel }
 *  - isUnlocked: true once the round's unlock timestamp has passed
 *  - unlockAt:   Date object, or null if round unknown
 *  - daysUntil:  whole days until unlock (0 if already unlocked)
 *  - label:      "Open" | "Unlocks tomorrow" | "Unlocks in N days"
 *  - dateLabel:  "June 28" — UTC date for display
 */
export function getRoundUnlockStatus(round, now = new Date()) {
  const ts = KNOCKOUT_UNLOCK_DATES[round];
  if (!ts) return { isUnlocked: false, unlockAt: null, daysUntil: null, label: round, dateLabel: null };
  const unlockAt = new Date(ts);
  const isUnlocked = now >= unlockAt;
  const msDiff = unlockAt - now;
  const daysUntil = isUnlocked ? 0 : Math.max(1, Math.ceil(msDiff / (1000 * 60 * 60 * 24)));
  const label = isUnlocked
    ? "Open"
    : (daysUntil === 1 ? "Unlocks tomorrow" : `Unlocks in ${daysUntil} days`);
  const dateLabel = unlockAt.toLocaleDateString("en-US", {
    month: "long", day: "numeric", timeZone: "UTC",
  });
  return { isUnlocked, unlockAt, daysUntil, label, dateLabel };
}

/** True if any knockout round has unlocked. Useful for branching pre/during-tournament UI. */
export function anyKnockoutUnlocked(now = new Date()) {
  return KNOCKOUT_ROUNDS.some(r => getRoundUnlockStatus(r, now).isUnlocked);
}

/** Display string: "🇧🇷 Brazil (BRA)" */
export function formatTeam(code) {
  const t = getTeam(code);
  if (!t) return code ?? "—";
  return `${t.flag} ${t.name} (${t.abbr})`;
}

/** Maximum score achievable. Mirrors SCORING.TOTAL_MAX for convenience. */
export const MAX_SCORE = SCORING.TOTAL_MAX;

// ---------- Knockout matchup helpers ----------

/** Return all matchups for a given round (R32, R16, QF, SF, FINAL). */
export function getRoundMatchups(round) {
  return KNOCKOUT_MATCHUPS[round] ?? [];
}

/** R32-only convenience accessor. */
export function getR32Matchups() {
  return KNOCKOUT_MATCHUPS.R32;
}

/** Find a matchup by its slot ID (e.g., "R32-7", "QF-1"). Returns null if unknown. */
export function getMatchupBySlot(slot) {
  if (!slot || typeof slot !== "string") return null;
  const round = slot.split("-")[0];
  const list = KNOCKOUT_MATCHUPS[round];
  if (!Array.isArray(list)) return null;
  return list.find(m => m.slot === slot) ?? null;
}

/** Internal: look up the recorded winner of a previous match from the live
 *  standings doc. Returns null if the round hasn't been written yet, the
 *  match isn't present, or the match has no winner recorded yet. */
function lookupWinner(matchSlot, liveStandings) {
  if (!matchSlot || !liveStandings?.knockouts) return null;
  const round = matchSlot.split("-")[0];
  const roundResults = liveStandings.knockouts[round];
  if (!Array.isArray(roundResults)) return null;
  const result = roundResults.find(r => r && r.matchSlot === matchSlot);
  return result?.winnerCode ?? null;
}

/** Internal: resolve a position-code string ("1A", "2B") against group standings.
 *  Returns the team code (e.g. "MEX") or null if the group isn't ranked yet. */
function lookupGroupPosition(positionCode, liveStandings) {
  const m = /^([12])([A-L])$/.exec(positionCode);
  if (!m) return null;
  const rank = parseInt(m[1], 10);
  const group = m[2];
  const groupData = liveStandings?.groups?.[group];
  if (!groupData) return null;
  // Tolerate both shapes the live doc may carry (array or { standings }).
  const arr = Array.isArray(groupData) ? groupData : groupData.standings;
  if (!Array.isArray(arr)) return null;
  return arr[rank - 1] ?? null;
}

/** Resolve a single side (home or away) of a matchup to a team code.
 *  Returns null if the team can't yet be determined from live data.
 *  Pass the owning matchSlot so third-place clusters can look up their
 *  CF-assigned team for that specific R32 slot. */
function resolveSide(side, matchSlot, liveStandings) {
  if (!side) return null;
  if (typeof side === "string") {
    return lookupGroupPosition(side, liveStandings);
  }
  if (typeof side === "object") {
    if (side.feedFrom) {
      return lookupWinner(side.feedFrom, liveStandings);
    }
    if (Array.isArray(side.thirdPlace)) {
      const assignments = liveStandings?.knockouts?.thirdPlaceAssignments;
      if (!assignments || typeof assignments !== "object") return null;
      return assignments[matchSlot] ?? null;
    }
  }
  return null;
}

/** Resolve both sides of a matchup to real team codes given live data.
 *  Returns { home, away } where either may be null pre-data.
 *
 *  Works for every round:
 *    R32 group-position slots  → reads liveStandings.groups[letter].standings
 *    R32 third-place slots     → reads liveStandings.knockouts.thirdPlaceAssignments
 *    R16/QF/SF/FINAL feeds     → reads liveStandings.knockouts[prevRound][].winnerCode
 */
export function getMatchTeams(round, slot, liveStandings) {
  const matchup = (KNOCKOUT_MATCHUPS[round] ?? []).find(m => m.slot === slot);
  if (!matchup) return { home: null, away: null };
  return {
    home: resolveSide(matchup.home, matchup.slot, liveStandings),
    away: resolveSide(matchup.away, matchup.slot, liveStandings),
  };
}

/** Human-readable label for a matchup side before it resolves to a real team.
 *  Used in pre-data UI states — "Winner A", "Runner-up B", "3rd of A/B/C/D/F",
 *  "Winner R32-7". Returns "TBD" for unknown shapes. */
export function formatPositionLabel(side) {
  if (!side) return "TBD";
  if (typeof side === "string") {
    const m = /^([12])([A-L])$/.exec(side);
    if (!m) return side;
    return m[1] === "1" ? `Winner Group ${m[2]}` : `Runner-up Group ${m[2]}`;
  }
  if (typeof side === "object") {
    if (Array.isArray(side.thirdPlace) && side.thirdPlace.length > 0) {
      return `3rd of ${side.thirdPlace.join("/")}`;
    }
    if (side.feedFrom) {
      return `Winner ${side.feedFrom}`;
    }
  }
  return "TBD";
}

/** Same as formatPositionLabel but returns a short variant for compact UI cells.
 *  "1A" → "1A", { thirdPlace: ["A","B","C","D","F"] } → "3ABCDF", feed → "WR32-7". */
export function formatPositionLabelShort(side) {
  if (!side) return "—";
  if (typeof side === "string") return side;
  if (typeof side === "object") {
    if (Array.isArray(side.thirdPlace) && side.thirdPlace.length > 0) {
      return `3${side.thirdPlace.join("")}`;
    }
    if (side.feedFrom) return `W${side.feedFrom}`;
  }
  return "—";
}

/** True if a side is a third-place cluster (R32 only). Useful when the UI needs
 *  to surface "this slot waits for the third-place rankings" copy. */
export function isThirdPlaceCluster(side) {
  return !!(side && typeof side === "object" && Array.isArray(side.thirdPlace));
}

/** Convenience: return the set of teams a user could pick as the winner of a
 *  given matchup, given current live data. Returns null if either side is not
 *  yet resolvable. Used by UI to decide whether to show a real pick card or
 *  a "waiting for previous round" placeholder. */
export function getPickableTeamsForMatch(round, slot, liveStandings) {
  const { home, away } = getMatchTeams(round, slot, liveStandings);
  if (!home || !away) return null;
  return [home, away];
}
